/**
 * SCR-024 — Portal Dashboard /portal
 * Stats: active classes, attendance rate, avg score, due assignments.
 * Lists: active classes, upcoming assignments, latest report, quick links.
 * Mobile-first: 1-col → 2-col → 4-col stat grid.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { Calendar, ChartLine, ClipboardList, BookOpen, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePortalDashboard } from '@client/lib/queries/use-portal';
import { useChildSelector } from '@client/store/child-selector';
import { authClient } from '@client/lib/auth-client';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';

export const Route = createFileRoute('/portal/')({
  component: PortalDashboard,
});

const WEEKDAY_LABEL: Record<string, string> = {
  mon: 'Thứ 2', tue: 'Thứ 3', wed: 'Thứ 4', thu: 'Thứ 5',
  fri: 'Thứ 6', sat: 'Thứ 7', sun: 'Chủ nhật',
};

const STATUS_LABEL: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  assigned: { label: 'Chưa nộp', variant: 'outline' },
  submitted: { label: 'Đã nộp', variant: 'default' },
  late: { label: 'Nộp muộn', variant: 'secondary' },
  graded: { label: 'Đã chấm', variant: 'default' },
};

const RATING_COLORS: Record<string, string> = {
  excellent: 'bg-brand-green-100 text-brand-green-700 dark:bg-brand-green-900/50 dark:text-brand-green-300',
  good: 'bg-[var(--color-info)]/10 text-[var(--color-info)] dark:bg-[var(--color-info-border)]/15 dark:text-[var(--color-info-border)]',
  average: 'bg-brand-orange-100 text-brand-orange-700 dark:bg-brand-orange-950/50 dark:text-brand-orange-300',
  needs_improvement: 'bg-destructive/10 text-destructive',
};

const RATING_LABEL: Record<string, string> = {
  excellent: 'Xuất sắc', good: 'Tốt', average: 'Khá', needs_improvement: 'Cần cải thiện',
};

function PortalDashboard() {
  const { data: session } = authClient.useSession();
  const role = (session?.user as { role?: string })?.role ?? '';
  const { selectedChildId } = useChildSelector();
  const studentId = role === 'parent' ? selectedChildId : undefined;

  const { data, isLoading, isError, refetch } = usePortalDashboard(studentId);

  if (isLoading) return <DashboardSkeleton />;

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">Không tải được dữ liệu</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Thử lại
        </Button>
      </div>
    );
  }

  const info = data;
  if (!info) return null;

  const { student, stats, classes, upcomingAssignments, latestReport } = info;

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      {/* Greeting */}
      <div>
        <h1 className="text-xl font-bold text-foreground">
          Xin chào, {student.fullName} 👋
        </h1>
        {student.branchName && (
          <p className="text-sm text-muted-foreground mt-0.5">{student.branchName}</p>
        )}
      </div>

      {/* Stat cards — 1 col → 2 col → 4 col */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          title="Lớp đang học"
          value={String(stats.activeClasses)}
          icon={<Calendar className="h-5 w-5 text-[var(--color-info)]" />}
          href="/portal/schedule"
          color="bg-muted"
        />
        <StatCard
          title="Chuyên cần"
          value={stats.attendanceRate != null ? `${stats.attendanceRate}%` : '—'}
          icon={<ChartLine className="h-5 w-5 text-brand-green-500" />}
          href="/portal/schedule"
          color="bg-muted"
        />
        <StatCard
          title="Điểm TB"
          value={stats.averageScore != null ? `${stats.averageScore}/100` : '—'}
          icon={<ChartLine className="h-5 w-5 text-muted-foreground" />}
          href="/portal/grades"
          color="bg-muted"
        />
        <StatCard
          title="Bài tập đến hạn"
          value={String(stats.dueAssignments)}
          icon={<ClipboardList className="h-5 w-5 text-brand-orange-500" />}
          href="/portal/assignments"
          color="bg-muted"
        />
      </div>

      {/* Active classes */}
      <section>
        <h2 className="text-base font-semibold mb-2">Lớp đang học</h2>
        {classes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
            Chưa có lớp học nào
          </p>
        ) : (
          <div className="space-y-2">
            {classes.map((cls) => (
              <Card key={cls.id} className="border-border">
                <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{cls.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{cls.courseName}</p>
                  </div>
                  {cls.nextSession && (
                    <div className="text-xs text-muted-foreground shrink-0">
                      {cls.nextSession.weekday
                        ? WEEKDAY_LABEL[cls.nextSession.weekday] ?? cls.nextSession.weekday
                        : ''}{' '}
                      {cls.nextSession.startTime}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Upcoming assignments */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold">Bài tập sắp đến hạn</h2>
          <Link to="/portal/assignments" className="text-xs text-primary hover:underline">
            Xem tất cả
          </Link>
        </div>
        {upcomingAssignments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
            Không có bài tập sắp đến hạn
          </p>
        ) : (
          <div className="space-y-2">
            {upcomingAssignments.map((a) => {
              const s = STATUS_LABEL[a.submissionStatus] ?? STATUS_LABEL['assigned']!;
              return (
                <Link key={a.id} to="/portal/assignments">
                  <Card className="border-border hover:bg-accent/40 transition-colors cursor-pointer">
                    <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
                      <p className="font-medium text-sm truncate">{a.title}</p>
                      <div className="flex items-center gap-2 shrink-0">
                        {a.dueDate && (
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(a.dueDate), { locale: vi, addSuffix: true })}
                          </span>
                        )}
                        <Badge variant={s.variant} className="text-xs">{s.label}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Latest report */}
      {latestReport && (
        <section>
          <h2 className="text-base font-semibold mb-2">Báo cáo tiến độ mới nhất</h2>
          <Link to="/portal/progress">
            <Card className="border-border hover:bg-accent/40 transition-colors cursor-pointer">
              <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-sm">{latestReport.periodLabel ?? 'Báo cáo tiến độ'}</p>
                  {latestReport.publishedAt && (
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(latestReport.publishedAt), { locale: vi, addSuffix: true })}
                    </p>
                  )}
                </div>
                {latestReport.overallRating && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${RATING_COLORS[latestReport.overallRating] ?? ''}`}>
                    {RATING_LABEL[latestReport.overallRating] ?? latestReport.overallRating}
                  </span>
                )}
              </CardContent>
            </Card>
          </Link>
        </section>
      )}

      {/* Quick links */}
      <section>
        <h2 className="text-base font-semibold mb-2">Điều hướng nhanh</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { to: '/portal/schedule', icon: Calendar, label: 'Lịch học' },
            { to: '/portal/grades', icon: ChartLine, label: 'Bảng điểm' },
            { to: '/portal/progress', icon: BookOpen, label: 'Tiến độ' },
            { to: '/portal/assignments', icon: ClipboardList, label: 'Bài tập' },
          ].map((item) => (
            <Link key={item.to} to={item.to}>
              <Card className="border-border hover:bg-accent/40 transition-colors cursor-pointer">
                <CardContent className="flex flex-col items-center justify-center py-4 gap-2">
                  <item.icon className="h-6 w-6 text-primary" />
                  <span className="text-sm font-medium">{item.label}</span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  href,
  color,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  href: string;
  color: string;
}) {
  return (
    <Link to={href}>
      <Card className="border-border hover:bg-accent/40 transition-colors cursor-pointer h-full">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <span className={`p-1 rounded-md ${color}`}>{icon}</span>
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-3 px-4">
          <p className="text-2xl font-bold text-foreground">{value}</p>
        </CardContent>
      </Card>
    </Link>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      <Skeleton className="h-7 w-48" />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-5 w-32" />
      <div className="space-y-2">
        {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
      </div>
    </div>
  );
}
